import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  BugReport,
  Person,
  Notifications,
  TrendingUp,
  TrendingDown,
  Wifi,
  People,
  Receipt,
  ShoppingCart,
  Business,
  Inventory,
  CalendarToday,
  Phone,
  Security,
  Settings,
  Assignment,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';

interface DashboardStats {
  totalCustomers: number;
  totalInvoices: number;
  totalOrders: number;
  totalSuppliers: number;
  totalItems: number;
  totalSubscriptions: number;
  totalUsers: number;
  totalRoles: number;
}

const stockData = [
  { category: 'newspapers', value: 18000, color: '#a78bfa' },
  { category: 'magazines', value: 30000, color: '#0d9488' },
  { category: 'newspapers', value: 22000, color: '#000000' },
  { category: 'magazines', value: 31000, color: '#3b82f6' },
  { category: 'newspapers', value: 13000, color: '#c084fc' },
  { category: 'magazines', value: 27000, color: '#10b981' },
];

const agencyData = [
  { name: 'United States', value: 52.1, color: '#374151' },
  { name: 'Canada', value: 22.8, color: '#0d9488' },
  { name: 'Mexico', value: 13.9, color: '#10b981' },
  { name: 'Other', value: 11.2, color: '#3b82f6' },
];

const notifications = [
  { id: 1, message: 'New customer registered successfully.', time: 'Just now', type: 'customer' },
  { id: 2, message: 'Order #TKT-07 completed.', time: '15 minutes ago', type: 'order' },
  { id: 3, message: 'Low stock alert for Laptop items.', time: '2 hours ago', type: 'stock' },
  { id: 4, message: 'New supplier added to the system.', time: 'Today, 11:30 AM', type: 'supplier' },
  { id: 5, message: 'Invoice #INV-001 generated.', time: 'Today, 10:45 AM', type: 'invoice' },
  { id: 6, message: 'Call center query resolved.', time: 'Yesterday, 4:20 PM', type: 'call' },
];

const activities = [
  { id: 1, message: 'Customer profile updated.', time: 'Just now', color: '#8884d8' },
  { id: 2, message: 'New order created successfully.', time: '25 minutes ago', color: '#50C878' },
  { id: 3, message: 'Stock quantity updated.', time: '1 hour ago', color: '#50C878' },
  { id: 4, message: 'Supplier payment processed.', time: '3 hours ago', color: '#ff6b6b' },
  { id: 5, message: 'Invoice sent to customer.', time: '5 hours ago', color: '#ff6b6b' },
  { id: 6, message: 'Location data modified.', time: 'Today, 2:15 PM', color: '#ffd93d' },
  { id: 7, message: 'Call center agent assigned.', time: 'Today, 1:30 PM', color: '#ffd93d' },
  { id: 8, message: 'User permissions updated.', time: 'Yesterday, 6:45 PM', color: '#4A90E2' },
];

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    totalInvoices: 0,
    totalOrders: 0,
    totalSuppliers: 0,
    totalItems: 0,
    totalSubscriptions: 0,
    totalUsers: 0,
    totalRoles: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [
        customersResponse,
        invoicesResponse,
        ordersResponse,
        suppliersResponse,
        itemsResponse,
        subscriptionsResponse,
        usersResponse,
        rolesResponse,
      ] = await Promise.all([
        apiService.getCustomers(),
        apiService.getInvoices(),
        apiService.getOrders(),
        apiService.getSuppliers(),
        apiService.getItems(),
        apiService.getSubscriptions(),
        apiService.getUsers(),
        apiService.getRoles(),
      ]);

      setStats({
        totalCustomers: customersResponse.data?.length || 0,
        totalInvoices: invoicesResponse.data?.length || 0,
        totalOrders: ordersResponse.data?.length || 0,
        totalSuppliers: suppliersResponse.data?.length || 0,
        totalItems: itemsResponse.data?.length || 0,
        totalSubscriptions: subscriptionsResponse.data?.length || 0,
        totalUsers: usersResponse.data?.length || 0,
        totalRoles: rolesResponse.data?.length || 0,
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      backgroundColor: '#f0f9ff',
      justifyContent: 'center'
    }}>
      {/* Main Content Area */}
      <Box sx={{ flex: 1, p: 3 }}>
        {/* Overview Section Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e40af' }}>
            Overview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#1e40af' }}>
              Today
            </Typography>
            <Box sx={{ 
              width: 8, 
              height: 8, 
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '6px solid #1e40af',
              ml: 0.5
            }} />
          </Box>
        </Box>

        {/* Overview Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#1e40af', mb: 1 }}>
                  Subscriptions
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0d9488',
                  mb: 1
                }}>
                  {stats.totalSubscriptions}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ color: '#1e40af', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
                    +11.01%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#1e40af', mb: 1 }}>
                  Suppliers
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0d9488',
                  mb: 1
                }}>
                  {stats.totalSuppliers}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingDown sx={{ color: '#1e40af', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
                    -0.03%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#1e40af', mb: 1 }}>
                  Orders
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#0d9488',
                  mb: 1
                }}>
                  {stats.totalOrders}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ color: '#1e40af', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
                    +15.03%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#1e40af', mb: 1 }}>
                  Invoices
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  color: '#3b82f6',
                  mb: 1
                }}>
                  {stats.totalInvoices}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUp sx={{ color: '#1e40af', fontSize: 16, mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
                    +6.08%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3}>
          {/* Customers Chart - Full Width */}
          <Grid size={{ xs: 12 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white',
              mb: 3
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  mb: 3
                }}>
                  Customers
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={[
                    { month: 'Jan', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.08), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.08), 1) },
                    { month: 'Feb', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.12), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.20), 1) },
                    { month: 'Mar', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.15), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.35), 1) },
                    { month: 'Apr', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.10), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.45), 1) },
                    { month: 'May', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.18), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.63), 1) },
                    { month: 'Jun', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.14), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.77), 1) },
                    { month: 'Jul', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.16), 1), total: Math.max(Math.floor(stats.totalCustomers * 0.93), 1) },
                    { month: 'Aug', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.13), 1), total: Math.max(Math.floor(stats.totalCustomers * 1.06), 1) },
                    { month: 'Sep', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.11), 1), total: Math.max(Math.floor(stats.totalCustomers * 1.17), 1) },
                    { month: 'Oct', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.09), 1), total: Math.max(Math.floor(stats.totalCustomers * 1.26), 1) },
                    { month: 'Nov', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.07), 1), total: Math.max(Math.floor(stats.totalCustomers * 1.33), 1) },
                    { month: 'Dec', enrolled: Math.max(Math.floor(stats.totalCustomers * 0.05), 1), total: Math.max(Math.floor(stats.totalCustomers * 1.38), 1) },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="month" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      domain={[0, 'dataMax + 5']}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="enrolled" 
                      stroke="#0d9488" 
                      strokeWidth={3} 
                      name="• Customers Enrolled"
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#3b82f6" 
                      strokeWidth={3} 
                      strokeDasharray="5 5" 
                      name="• Total Customers"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Store Stock Chart - Left */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  mb: 3
                }}>
                  Store stock
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { category: 'Items', value: Math.max(stats.totalItems, 1), color: '#a78bfa' },
                    { category: 'Suppliers', value: Math.max(stats.totalSuppliers, 1), color: '#0d9488' },
                    { category: 'Orders', value: Math.max(stats.totalOrders, 1), color: '#000000' },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="category" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      domain={[0, 'dataMax + 2']}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {[
                        { category: 'Items', value: Math.max(stats.totalItems, 1), color: '#a78bfa' },
                        { category: 'Suppliers', value: Math.max(stats.totalSuppliers, 1), color: '#0d9488' },
                        { category: 'Orders', value: Math.max(stats.totalOrders, 1), color: '#000000' },
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Overseas Agencies Chart - Right */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#1f2937',
                  mb: 3
                }}>
                  Overseas Agencies
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ResponsiveContainer width="60%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Users', value: Math.max(stats.totalUsers, 1), color: '#a78bfa' },
                          { name: 'Roles', value: Math.max(stats.totalRoles, 1), color: '#0d9488' },
                          { name: 'Subscriptions', value: Math.max(stats.totalSubscriptions, 1), color: '#3b82f6' },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {[
                          { name: 'Users', value: Math.max(stats.totalUsers, 1), color: '#a78bfa' },
                          { name: 'Roles', value: Math.max(stats.totalRoles, 1), color: '#0d9488' },
                          { name: 'Subscriptions', value: Math.max(stats.totalSubscriptions, 1), color: '#3b82f6' },
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ ml: 2 }}>
                    {[
                      { name: 'Users', value: Math.max(stats.totalUsers, 1), color: '#a78bfa' },
                      { name: 'Roles', value: Math.max(stats.totalRoles, 1), color: '#0d9488' },
                      { name: 'Subscriptions', value: Math.max(stats.totalSubscriptions, 1), color: '#3b82f6' },
                    ].map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: item.color,
                            mr: 1.5
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#6b7280', fontSize: 13, fontWeight: 400 }}>
                          {item.name}: {item.value}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Right Sidebar */}
      <Box sx={{ 
        width: 300, 
        backgroundColor: '#f0f9ff',
        p: 3,
        borderLeft: '1px solid #e2e8f0'
      }}>
        {/* Notifications */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#1f2937',
            mb: 2
          }}>
            Notifications
          </Typography>
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      width: 24, 
                      height: 24,
                      backgroundColor: notification.type === 'customer' ? '#4A90E2' : 
                                      notification.type === 'order' ? '#10b981' : 
                                      notification.type === 'stock' ? '#f59e0b' :
                                      notification.type === 'supplier' ? '#8b5cf6' :
                                      notification.type === 'invoice' ? '#ef4444' :
                                      notification.type === 'call' ? '#06b6d4' : '#10b981'
                    }}>
                      {notification.type === 'customer' ? <Person sx={{ fontSize: 14 }} /> :
                       notification.type === 'order' ? <ShoppingCart sx={{ fontSize: 14 }} /> :
                       notification.type === 'stock' ? <Warning sx={{ fontSize: 14 }} /> :
                       notification.type === 'supplier' ? <Business sx={{ fontSize: 14 }} /> :
                       notification.type === 'invoice' ? <Receipt sx={{ fontSize: 14 }} /> :
                       notification.type === 'call' ? <Phone sx={{ fontSize: 14 }} /> :
                       <CheckCircle sx={{ fontSize: 14 }} />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: '#1f2937', fontSize: 13 }}>
                        {notification.message}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 11 }}>
                        {notification.time}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Activities */}
        <Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#1f2937',
            mb: 2
          }}>
            Activities
          </Typography>
          <List sx={{ p: 0 }}>
            {activities.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      width: 24, 
                      height: 24,
                      backgroundColor: activity.color
                    }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: '#1f2937', fontSize: 13 }}>
                        {activity.message}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 11 }}>
                        {activity.time}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < activities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
